import { useEffect, useMemo, useState } from 'react';
import { Props as ItemProps } from 'features/webinars/item';
import { WebinarsApi } from 'api';
import dayjs from 'dayjs';
import ruWithMonth from 'utils/dayjs/ruWithMonth';
import urljoin from 'url-join';
import { baseUrl } from 'utils/api';
import { useClickStreamContext } from 'features/shared/ClickStream';

const getDowloadPath = (subjectId: number, month: string) => {
  const monthTitle = dayjs(month).locale('ru').format('MMMM YYYY');
  return urljoin(
    baseUrl || '',
    `/api/content/public/webinars/pdf/${month}/Расписание вебинаров ${
      monthTitle.charAt(0).toUpperCase() + monthTitle.slice(1)
    }?subject_id=${subjectId}`,
  );
};

export interface Tab {
  id: string;
  label: string;
}
const getMonthByDate = (date: Date | string) => dayjs(date).format('YYYY-MM');
const getMonthNameByDate = (date: Date | string) => dayjs(date).locale('ru').format('MMMM');
const getTabItem = (date: Date | string) => ({ id: getMonthByDate(date), label: getMonthNameByDate(date) });
const now = new Date();
const defaultTab = getTabItem(now);

export const getCurrentLerningYear = () => {
  // const date = new Date();
  // const currentYear = date.getFullYear();
  // const startLerningYear = date.toJSONWithoutTimezone() < `${currentYear}-09-01` ? currentYear - 1 : currentYear;

  const startLerningYear = 2025;
  return startLerningYear;
};

export const useGetWebinar = (userId: number) => {
  const [loaded, setLoaded] = useState(false);
  const [webinars, setWebinars] = useState<ItemProps[]>([]);
  const [availableTabs, setAvailableTabs] = useState<string[]>([defaultTab.id]);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const context = useClickStreamContext();

  const startMonth = useMemo(() => new Date().setDateWithoutTimezone(`${getCurrentLerningYear()}-08-01`), []);
  const months = useMemo(
    () =>
      new Array(12)
        .fill(0)
        .map((el, i) => {
          const month = new Date(startMonth);
          month.setMonth(month.getMonth() + i);
          return getTabItem(month);
        })
        .filter((el) => availableTabs.includes(el.id)),
    [availableTabs],
  );
  const fetch = async () => {
    const result = await WebinarsApi.getWebinars(userId);
    const prepareResult = result
      .filter((el) => el.startDate)
      .map((el) => ({ ...el, time: new Date(el.startDate!).getTime() }));
    prepareResult.sort((a, b) => a.time - b.time);
    const orderWebinars = [
      ...prepareResult.filter((el) => el.time >= now.getTime()),
      ...prepareResult.filter((el) => el.time < now.getTime()),
    ];

    setWebinars(
      orderWebinars.map((el) => {
        const startDate = el.startDate ? new Date().setDateWithoutTimezone(el.startDate) : '';
        const endDate = el.endDate ? new Date().setDateWithoutTimezone(el.endDate) : '';
        return {
          month: getMonthByDate(startDate),
          date: `${dayjs(startDate).locale('ru_with_month', ruWithMonth).format('DD MMMM')}, ${dayjs(startDate).format(
            'HH:mm',
          )}${endDate ? `-${dayjs(endDate).format('HH:mm')}` : ''} мск`,
          name: el.name || '',
          target: el.target || '',
          details: el.details?.split(/[\n\r]+/).filter(detail => detail.trim() !== "") || [],
          webinarId: el.externalId?.toString() || '',
        };
      }),
    );

    const tabs = prepareResult
      .map((el) => getTabItem(el.startDate!))
      .filter((el, i, arr) => arr.findIndex((tab) => tab.id === el.id) === i)
      .map((el) => el.id);
    setAvailableTabs(tabs);

    if (tabs.length && !tabs.find((el) => el === activeTab.id)) {
      setActiveTab(months.find((el) => el.id === tabs[tabs.length - 1]) || months[months.length - 1]);
    }

    setLoaded(true);
  };

  useEffect(() => {
    fetch();
  }, []);

  const webinarsInMonth = useMemo(() => {
    return webinars.filter((el) => el.month === activeTab.id);
  }, [webinars, activeTab]);

  const setTab = (value: Tab) => {
    context.logPageEvent?.({
      eventType: 'webinars-change-month',
      eventInfo: 'Клик по табу смены месяца вебинара',
      cd1: value.label,
    });
    if (availableTabs.includes(value.id)) {
      setActiveTab(value);
    }
  };
  return {
    loaded,
    webinars,
    getTabItem,
    defaultTab,
    availableTabs,
    activeTab,
    webinarsInMonth,
    months,
    getDowloadPath,
    setTab,
  };
};
